// ── Sub-App Navigation Configuration ────────────────────────────
//
// Declarative configuration for each sub-app's navigation.
// - sideNavItems: flat list of page links shown in the side nav
// - sections: only used by cortex (dynamic project sections)
// - customSideNav: sub-app owns its own sidebar (e.g. doc tree)

// ── Types ───────────────────────────────────────────────────────

export type ContextType = "team" | "project" | "partner" | "none";

export interface PageNavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
  badge?: string | number;
}

export interface SectionConfig {
  slug: string;
  label: string;
  icon: string;
  defaultPath: string;
  pages: PageNavItem[];
}

export interface SubAppNavConfig {
  slug: string;
  contextType: ContextType;
  /** Display title shown in the header (e.g. "Content Management") */
  title?: string;
  /** Flat side-nav links (non-docs apps) */
  sideNavItems: PageNavItem[];
  /** Section tabs — only used by cortex (dynamic per project) */
  sections: SectionConfig[];
  sideNavWidth: number;
  searchEnabled: boolean;
  customSideNav?: boolean;
}

// ── Static Registry ─────────────────────────────────────────────

export const APP_NAV_CONFIGS: Record<string, SubAppNavConfig> = {
  "studio": {
    slug: "studio",
    contextType: "project",
    title: "Cortex Studio",
    sideNavItems: [],
    sections: [], // Dynamic per project (content sections + API sections)
    sideNavWidth: 280,
    searchEnabled: true,
    customSideNav: true, // Doc tree sidebar
  },

  "docs": {
    slug: "docs",
    contextType: "project",
    title: "Docs",
    sideNavItems: [],
    sections: [],
    sideNavWidth: 280,
    searchEnabled: true,
    customSideNav: true,
  },

  platform: {
    slug: "platform",
    contextType: "none",
    sideNavItems: [],
    sections: [],
    sideNavWidth: 0,
    searchEnabled: false,
  },
};

// ── Helpers ──────────────────────────────────────────────────────

/**
 * Resolve the active section from the current URL pathname by matching
 * against the section defaultPaths. Used by cortex for project sections.
 */
export function resolveActiveSection(
  pathname: string,
  config: SubAppNavConfig,
): SectionConfig | null {
  return (
    config.sections
      .filter(
        (s) =>
          pathname === s.defaultPath ||
          pathname.startsWith(s.defaultPath + "/"),
      )
      .sort((a, b) => b.defaultPath.length - a.defaultPath.length)[0] ?? null
  );
}
